import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GMServiceVisitByDealer, GMServiceVisitByVin } from '@quorum/models/oem-resource';
import { Employee } from '@quorum/models/xs-resource';
import { CustomValidators } from '@quorum/sha-validators';
import { SvcVehicleDiagnosticsRootFacade } from '@quorum/svc-vehicle-diagnostics-root';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'quorum-gm-vehicle-diagnostics-filtering-dialog',
  templateUrl: './gm-vehicle-diagnostics-filtering-dialog.component.html',
  styleUrls: ['./gm-vehicle-diagnostics-filtering-dialog.component.scss'],
})
export class GmVehicleDiagnosticsFilteringDialogComponent implements OnInit, OnDestroy {
  constructor(
    public dialogRef: MatDialogRef<GmVehicleDiagnosticsFilteringDialogComponent>,
    private formBuilder: FormBuilder,
    public svcVehicleDiagnosticsRootFacade: SvcVehicleDiagnosticsRootFacade,
    @Inject(MAT_DIALOG_DATA)
    public saviDialogData: {
      serviceAdvisors$: Observable<Employee[]>;
      vin: string;
      dealerCountryCode: string;
      businessId: string;
    }
  ) {}

  saviRecords$: BehaviorSubject<
    { serviceVisit: GMServiceVisitByVin | GMServiceVisitByDealer; isWithinADay: boolean }[]
  > = new BehaviorSubject<{ serviceVisit: GMServiceVisitByVin | GMServiceVisitByDealer; isWithinADay: boolean }[]>([]);

  selectedSaviRecord = -1;
  selectedSaviRecordObject!: GMServiceVisitByVin | GMServiceVisitByDealer;

  defaultSaviFilterValues: {
    dateFrom: Date;
    dateTo: Date;
    saviVinOrDealer: boolean;
    serviceAdvisor: { value: Employee; disabled: boolean };
    vin: string;
  } = {
    dateFrom: new Date(new Date().setHours(0, 0, 0, 0)),
    dateTo: new Date(new Date().setHours(23, 59, 59, 999)),
    saviVinOrDealer: false,
    serviceAdvisor: { value: null, disabled: true },
    vin: this.saviDialogData.vin,
  };

  saviFilterForm: FormGroup = this.formBuilder.group(this.defaultSaviFilterValues, {
    validators: CustomValidators.dateRangeValidator('dateFrom', 'dateTo'),
  });
  maxDate: Date = new Date(new Date().setFullYear(new Date().getFullYear() + 5));
  destroy$ = new Subject();
  dataLoading$: Observable<boolean>;

  ngOnInit(): void {
    this.dataLoading$ = combineLatest([
      this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVinStatus$,
      this.svcVehicleDiagnosticsRootFacade.serviceVisitsByDealerStatus$,
    ]).pipe(
      map(([serviceVisitsByVinStatus, serviceVisitsByDealerStatus]) => {
        if ([serviceVisitsByVinStatus, serviceVisitsByDealerStatus].some((value) => value === 'loading')) {
          return true;
        } else {
          return false;
        }
      })
    );
    // when filter dialog is opened, automatically display all service visits for that VIN today
    combineLatest([
      this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVin$.pipe(
        map((serviceVisits) => {
          return serviceVisits.filter((serviceVisit) => {
            const checkInDate = this.svcVehicleDiagnosticsRootFacade.createCheckInDate(
              serviceVisit.checkInDate,
              serviceVisit.checkInTime
            );
            return (
              checkInDate.getFullYear() == new Date().getFullYear() &&
              checkInDate.getDate() == new Date().getDate() &&
              checkInDate.getMonth() == new Date().getMonth()
            );
          });
        })
      ),
      this.svcVehicleDiagnosticsRootFacade.serviceVisitsByDealer$.pipe(
        map((serviceVisits) => {
          return serviceVisits.filter((serviceVisit) => {
            const checkInDate = this.svcVehicleDiagnosticsRootFacade.createCheckInDate(
              serviceVisit.checkInDate,
              serviceVisit.checkInTime
            );
            return (
              checkInDate.getFullYear() == new Date().getFullYear() &&
              checkInDate.getDate() == new Date().getDate() &&
              checkInDate.getMonth() == new Date().getMonth()
            );
          });
        })
      ),
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisit$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([serviceVisitsByVin, serviceVisitsByDealer, linkedServiceVisit]) => {
        this.saviFilterForm.get('saviVinOrDealer').value === true
          ? this.sortServiceVisits(serviceVisitsByDealer, linkedServiceVisit?.serviceVisitId)
          : this.sortServiceVisits(serviceVisitsByVin, linkedServiceVisit?.serviceVisitId);
      });

    this.saviFilterForm
      .get('saviVinOrDealer')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((saviVinOrDealer: boolean) => {
        saviVinOrDealer === true
          ? this.saviFilterForm.get('serviceAdvisor').enable()
          : this.saviFilterForm.get('serviceAdvisor').disable();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchSaviRecords(
    saviFormValues: {
      dateFrom: Date;
      dateTo: Date;
      saviVinOrDealer: boolean;
      serviceAdvisor: Employee;
      vin: string;
    },
    serviceVisitsByVin: GMServiceVisitByVin[],
    serviceVisitsByDealer: GMServiceVisitByDealer[],
    linkedServiceVisitId?: string
  ) {
    this.selectedSaviRecord = -1;
    // if toggle is true, filter Dealer list, if false filter Vin list
    if (saviFormValues.saviVinOrDealer) {
      let filteredServiceVisitsByDealer = serviceVisitsByDealer.filter((serviceVisit: any) => {
        const checkInDate = this.svcVehicleDiagnosticsRootFacade.createCheckInDate(
          serviceVisit.checkInDate,
          serviceVisit.checkInTime
        );
        return (
          checkInDate.getTime() >= saviFormValues.dateFrom.getTime() &&
          checkInDate.getTime() <= saviFormValues.dateTo.getTime()
        );
      });
      if (saviFormValues.serviceAdvisor) {
        filteredServiceVisitsByDealer = filteredServiceVisitsByDealer.filter(
          (serviceVisit) => serviceVisit.advisorId === saviFormValues.serviceAdvisor.nominalRollCode
        );
      }
      this.sortServiceVisits(filteredServiceVisitsByDealer, linkedServiceVisitId);
    } else {
      const filteredServiceVisitsByVin = serviceVisitsByVin.filter((serviceVisit: any) => {
        const checkInDate = this.svcVehicleDiagnosticsRootFacade.createCheckInDate(
          serviceVisit.checkInDate,
          serviceVisit.checkInTime
        );
        return (
          checkInDate.getTime() >= saviFormValues.dateFrom.getTime() &&
          checkInDate.getTime() <= saviFormValues.dateTo.getTime()
        );
      });
      this.sortServiceVisits(filteredServiceVisitsByVin, linkedServiceVisitId);
    }
  }

  selectSaviRecord(saviRecord: GMServiceVisitByVin | GMServiceVisitByDealer, selectedSaviRecordIndex: number) {
    this.selectedSaviRecordObject = saviRecord;
    this.selectedSaviRecord = selectedSaviRecordIndex;
  }

  linkSaviRecord(selectedSaviRecord: GMServiceVisitByVin | GMServiceVisitByDealer | null) {
    this.dialogRef.close(selectedSaviRecord);
  }

  sortServiceVisits(serviceVisits: GMServiceVisitByVin[] | GMServiceVisitByDealer[], linkedServiceVisitId: string) {
    const serviceVisitsWithinADay: {
      serviceVisit: GMServiceVisitByVin | GMServiceVisitByDealer;
      isWithinADay: boolean;
    }[] = [];
    serviceVisits.sort((serviceVisitA: any, serviceVisitB: any) => {
      const checkInDateA = this.svcVehicleDiagnosticsRootFacade.createCheckInDate(
        serviceVisitA.checkInDate,
        serviceVisitA.checkInTime
      );

      const checkInDateB = this.svcVehicleDiagnosticsRootFacade.createCheckInDate(
        serviceVisitB.checkInDate,
        serviceVisitB.checkInTime
      );

      return checkInDateB.getTime() - checkInDateA.getTime();
    });

    const index = serviceVisits.findIndex(
      (service: GMServiceVisitByVin | GMServiceVisitByDealer) => service.serviceVisitId === linkedServiceVisitId
    );

    if (index > -1) {
      // TODO: clean up typing
      const serviceVisit: any = serviceVisits.splice(index, 1).pop();
      serviceVisits.unshift(serviceVisit);
    }
    serviceVisits.forEach((serviceVisit: GMServiceVisitByVin | GMServiceVisitByDealer) => {
      serviceVisitsWithinADay.push({
        serviceVisit: serviceVisit,
        isWithinADay: this.svcVehicleDiagnosticsRootFacade.isWithinADay(
          serviceVisit.checkInDate,
          serviceVisit.checkInTime
        ),
      });
    });
    this.saviRecords$.next(serviceVisitsWithinADay);
  }

  cancel() {
    this.dialogRef.close();
  }
}
